import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Bell, CheckCircle, FileText, MessageSquare, Truck, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import useClientTicketsRealtime from '../../hooks/useClientTicketsRealtime';
import styles from './ClientTicketNotifier.module.css';

const unwrapList = (data) => Array.isArray(data) ? data : (data?.results || []);

const trimText = (value) => {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  if (!text) return 'Новое сообщение без текста';
  return text.length > 120 ? `${text.slice(0, 120)}...` : text;
};

const formatDate = (value) => {
  if (!value) return '';
  return new Date(value).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const buildNotificationFromTicket = (ticket, isGuest) => {
  const requestNumber = ticket?.repair_request_number || 'заявка';
  return {
    id: `ticket-${ticket.id}`,
    ticket_id: ticket.id,
    repair_request_id: ticket.repair_request,
    repair_request_number: ticket.repair_request_number,
    customer_name: ticket.customer_name,
    project_name: ticket.project_name,
    title: isGuest ? `Ответ по ${requestNumber}` : `Тикет ${requestNumber}`,
    subtitle: [ticket.customer_name, ticket.project_name].filter(Boolean).join(' · ') || 'CRM',
    text: trimText(ticket.last_message?.content),
    unread_count: Number(ticket.unread_count || 0),
    last_message_at: ticket.last_message_at || ticket.created_at,
  };
};

const buildNotificationFromApproval = (approval) => ({
  id: `approval-${approval.id}`,
  type: 'approval',
  repair_request_id: approval.id,
  repair_request_number: approval.request_number,
  customer_name: approval.customer?.name,
  project_name: approval.project?.client_display_name || approval.project?.client_name || 'Заявки на ремонт',
  title: `Согласование ${approval.request_number || ''}`.trim(),
  subtitle: [
    approval.customer?.name,
    approval.project?.client_display_name || approval.project?.client_name || 'Заявки на ремонт',
  ].filter(Boolean).join(' · ') || 'CRM',
  text: `Требуется решение по оборудованию: ${approval.approval_progress?.pending || 0}`,
  unread_count: 1,
  last_message_at: approval.created_at || new Date().toISOString(),
});

const buildNotificationFromPayload = (payload, isGuest) => {
  const requestNumber = payload.repair_request_number || 'заявка';
  const action = String(payload.action || '');
  const isDocument = action.startsWith('client_document') || payload.target === 'documents';
  const isClientRequest = action.startsWith('client_repair_request') || action.startsWith('client_pickup') || action.startsWith('client_missing');
  const isApproval = action.startsWith('client_approval') || action.startsWith('approval_state') || payload.type === 'approval';
  const id = isDocument
    ? `document-${payload.document_id || payload.repair_request_id}`
    : isApproval
    ? `approval-${payload.approval_id || payload.repair_request_id}`
    : (isClientRequest ? `client-request-${payload.repair_request_id}` : `ticket-${payload.ticket_id}`);
  return {
    ...payload,
    id,
    type: isDocument ? 'document' : (isApproval ? 'approval' : (isClientRequest ? 'client_request' : 'ticket')),
    toastId: `toast-${payload.ticket_id || payload.approval_id || payload.repair_request_id}-${payload.message_id || Date.now()}-${Date.now()}`,
    title: isDocument
      ? `Документ по ${requestNumber}`
      : isClientRequest
      ? (isGuest ? `Заявка ${requestNumber}` : `Клиентская заявка ${requestNumber}`)
      : isApproval
        ? `Согласование ${requestNumber}`
      : (isGuest ? `Ответ по ${requestNumber}` : `Новый тикет: ${requestNumber}`),
    subtitle: [payload.customer_name, payload.project_name].filter(Boolean).join(' · ') || 'CRM',
    text: trimText(payload.message_content),
    unread_count: 1,
    last_message_at: payload.last_message_at || new Date().toISOString(),
  };
};

const ClientTicketNotifier = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [toastItems, setToastItems] = useState([]);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const roleNames = useMemo(() => (user?.roles || []).map(role => role.name), [user]);
  const permissionKeys = useMemo(() => new Set(user?.permissions || []), [user]);
  const isGuest = roleNames.includes('Гость');
  const canHandleTickets = Array.isArray(user?.permissions)
    ? permissionKeys.has('crm.action.manage_client_tickets') || permissionKeys.has('crm.admin.override')
    : (
      user?.is_staff ||
      user?.is_superuser ||
      roleNames.includes('admin') ||
      roleNames.includes('Ассистент') ||
      roleNames.includes('Специалист по логистике')
    );
  const isEnabled = isGuest || canHandleTickets;

  const shouldShowTicket = useCallback((ticket) => {
    if (!ticket) return false;
    const unreadCount = Number(ticket.unread_count || 0);
    if (isGuest) return unreadCount > 0;
    return Boolean(ticket.needs_response) || unreadCount > 0 || ['new', 'waiting_crm'].includes(ticket.status);
  }, [isGuest]);

  const fetchCounts = useCallback(async () => {
    if (!isEnabled) return;
    try {
      const [ticketsResponse, approvalsResponse] = await Promise.all([
        api.get('/repairs/client-tickets/counts/'),
        isGuest ? api.get('/repairs/approvals/').catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      ]);
      const counts = ticketsResponse.data || {};
      const countFromApi = isGuest
        ? Number(counts.unread || 0)
        : Number(counts.needs_response ?? counts.unread ?? 0);
      const approvalsCount = isGuest && Array.isArray(approvalsResponse.data) ? approvalsResponse.data.length : 0;
      setUnreadTotal(countFromApi + approvalsCount);
    } catch (error) {
      console.error('Ошибка загрузки счетчика тикетов:', error);
    }
  }, [isEnabled, isGuest]);

  const fetchNotifications = useCallback(async () => {
    if (!isEnabled) return;
    setIsLoading(true);
    try {
      const [countsResponse, ticketsResponse, approvalsResponse] = await Promise.all([
        api.get('/repairs/client-tickets/counts/'),
        api.get('/repairs/client-tickets/', { params: { page_size: 100 } }),
        isGuest ? api.get('/repairs/approvals/').catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      ]);
      const ticketItems = unwrapList(ticketsResponse.data)
        .filter(shouldShowTicket)
        .map(ticket => buildNotificationFromTicket(ticket, isGuest));
      const approvalItems = isGuest && Array.isArray(approvalsResponse.data)
        ? approvalsResponse.data.map(buildNotificationFromApproval)
        : [];
      const items = [...approvalItems, ...ticketItems];
      const counts = countsResponse.data || {};
      const countFromApi = isGuest
        ? Number(counts.unread || 0)
        : Number(counts.needs_response ?? counts.unread ?? 0);
      const fallbackCount = items.reduce((sum, item) => sum + (isGuest ? Number(item.unread_count || 0) : 1), 0);
      setNotifications(items);
      setUnreadTotal(isGuest ? countFromApi + approvalItems.length : (countFromApi || fallbackCount));
    } catch (error) {
      console.error('Ошибка загрузки уведомлений тикетов:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isEnabled, isGuest, shouldShowTicket]);

  useEffect(() => {
    if (!isEnabled) {
      setNotifications([]);
      setToastItems([]);
      setUnreadTotal(0);
      setIsPanelOpen(false);
      return;
    }
    fetchCounts();
  }, [fetchCounts, isEnabled]);

  useEffect(() => {
    if (isEnabled && isPanelOpen) {
      fetchNotifications();
    }
  }, [fetchNotifications, isEnabled, isPanelOpen]);

  useEffect(() => {
    if (!isEnabled) return undefined;
    const timer = window.setInterval(fetchCounts, 60000);
    return () => window.clearInterval(timer);
  }, [fetchCounts, isEnabled]);

  useEffect(() => {
    if (!isEnabled) return undefined;
    const handleRead = (event) => {
      const ticketId = event?.detail?.ticket_id;
      if (ticketId) {
        setNotifications(prev => prev.filter(notification => Number(notification.ticket_id) !== Number(ticketId)));
      }
      setUnreadTotal(prev => Math.max(0, prev - 1));
      window.setTimeout(isPanelOpen ? fetchNotifications : fetchCounts, 50);
    };
    window.addEventListener('crm-client-ticket-read', handleRead);
    return () => window.removeEventListener('crm-client-ticket-read', handleRead);
  }, [fetchCounts, fetchNotifications, isEnabled, isPanelOpen]);

  const dismissToast = useCallback((id) => {
    setToastItems(prev => prev.filter(item => item.toastId !== id));
  }, []);

  const openNotification = useCallback((item) => {
    if (item.toastId) {
      dismissToast(item.toastId);
    }
    setIsPanelOpen(false);
    if (item.type === 'document') {
      setNotifications(prev => prev.filter(notification => notification.id !== item.id));
      setUnreadTotal(prev => Math.max(0, prev - 1));
      if (isGuest) {
        localStorage.setItem('client_portal_open_request_id', item.repair_request_id);
        localStorage.setItem('client_portal_open_documents', '1');
        window.dispatchEvent(new CustomEvent('crm-open-client-request', {
          detail: { ...item, openDocuments: true },
        }));
        navigate('/client');
      } else {
        localStorage.setItem('client_ticket_open_documents_request_id', item.repair_request_id);
        window.dispatchEvent(new CustomEvent('crm-open-client-documents', { detail: item }));
        navigate('/select-project');
      }
      return;
    }
    if (item.type === 'approval') {
      setNotifications(prev => prev.filter(notification => notification.id !== item.id));
      setUnreadTotal(prev => Math.max(0, prev - 1));
      if (isGuest) {
        localStorage.setItem('client_portal_open_approval_id', item.repair_request_id);
        window.dispatchEvent(new CustomEvent('crm-open-client-approval', { detail: item }));
        navigate('/client');
      } else {
        localStorage.setItem('project_selection_open_section', 'approvals');
        window.dispatchEvent(new CustomEvent('crm-open-project-section', {
          detail: { section: 'approvals' },
        }));
        navigate('/select-project');
      }
      return;
    }
    if (item.type === 'client_request') {
      setNotifications(prev => prev.filter(notification => notification.id !== item.id));
      setUnreadTotal(prev => Math.max(0, prev - 1));
      if (isGuest) {
        localStorage.setItem('client_portal_open_request_id', item.repair_request_id);
        window.dispatchEvent(new CustomEvent('crm-open-client-request', { detail: item }));
        navigate('/client');
      } else {
        localStorage.setItem('client_pickup_open_id', item.repair_request_id);
        window.dispatchEvent(new CustomEvent('crm-open-client-pickup', { detail: item }));
        navigate('/select-project');
      }
      return;
    }
    if (isGuest) {
      setNotifications(prev => prev.filter(notification => Number(notification.ticket_id) !== Number(item.ticket_id)));
      setUnreadTotal(prev => Math.max(0, prev - (Number(item.unread_count || 0) || 1)));
      localStorage.setItem('client_portal_open_request_id', item.repair_request_id);
      localStorage.setItem('client_portal_open_ticket', '1');
      window.dispatchEvent(new CustomEvent('crm-open-client-request', {
        detail: { ...item, openTicket: true },
      }));
      navigate('/client');
      return;
    }
    localStorage.setItem('client_ticket_open_id', item.ticket_id);
    window.dispatchEvent(new CustomEvent('crm-open-client-ticket', { detail: item }));
    navigate('/select-project');
  }, [dismissToast, isGuest, navigate]);

  const handleRealtime = useCallback((payload) => {
    if (!payload) return;
    if (!isEnabled) return;
    if (payload.silent) {
      window.setTimeout(isPanelOpen ? fetchNotifications : fetchCounts, 250);
      return;
    }
    if (payload.action === 'client_ticket_read') {
      if (isGuest && payload.read_by_guest) {
        setNotifications(prev => prev.filter(notification => Number(notification.ticket_id) !== Number(payload.ticket_id)));
        window.setTimeout(isPanelOpen ? fetchNotifications : fetchCounts, 250);
      }
      return;
    }
    const isClientRequestEvent = [
      'client_repair_request_created',
      'client_pickup_assigned',
      'client_repair_request_received',
      'client_repair_request_changed',
      'client_missing_equipment',
    ].includes(payload.action);
    const isDocumentEvent = String(payload.action || '').startsWith('client_document');
    const isApprovalEvent = String(payload.action || '').startsWith('client_approval') || String(payload.action || '').startsWith('approval_state');
    if (!isClientRequestEvent && !isDocumentEvent && !isApprovalEvent && payload.action !== 'client_ticket_changed') return;
    if (payload.message_author_id && Number(payload.message_author_id) === Number(user?.id)) {
      window.setTimeout(isPanelOpen ? fetchNotifications : fetchCounts, 350);
      return;
    }
    if (!isClientRequestEvent && !isDocumentEvent && !isApprovalEvent) {
      if (isGuest && payload.message_is_from_guest !== false) return;
      if (!isGuest && payload.message_is_from_guest !== true) return;
    }
    if (!isGuest && payload.action === 'client_pickup_assigned') return;
    if (!isGuest && payload.action === 'client_repair_request_received') return;

    const item = buildNotificationFromPayload(payload, isGuest);

    setNotifications(prev => [item, ...prev.filter(notification => notification.id !== item.id)].slice(0, 100));
    setUnreadTotal(prev => prev + 1);
    setToastItems(prev => [item, ...prev].slice(0, 4));
    window.setTimeout(() => dismissToast(item.toastId), 9000);
    window.setTimeout(isPanelOpen ? fetchNotifications : fetchCounts, 350);
  }, [dismissToast, fetchCounts, fetchNotifications, isEnabled, isGuest, isPanelOpen, user?.id]);

  useClientTicketsRealtime(isEnabled ? handleRealtime : null);

  if (!isEnabled) return null;

  const badgeLabel = unreadTotal > 99 ? '99+' : unreadTotal;

  const overlay = (
    <>
      {isPanelOpen && (
        <div className={styles.panel}>
          <div className={styles.panelHeader}>
            <div>
              <strong>Уведомления</strong>
              <span>{unreadTotal > 0 ? `Активных: ${badgeLabel}` : 'Новых уведомлений нет'}</span>
            </div>
            <button type="button" onClick={() => setIsPanelOpen(false)} aria-label="Закрыть уведомления">
              <X size={16} />
            </button>
          </div>

          {isLoading ? (
            <div className={styles.panelState}>Загрузка уведомлений...</div>
          ) : notifications.length ? (
            <div className={styles.notificationList}>
              {notifications.map(item => (
                <button
                  key={item.id}
                  type="button"
                  className={styles.notificationItem}
                  onClick={() => openNotification(item)}
                >
                  <div className={styles.notificationIcon}>
                    {item.type === 'approval'
                      ? <CheckCircle size={17} />
                      : item.type === 'document'
                        ? <FileText size={17} />
                        : item.type === 'client_request'
                          ? <Truck size={17} />
                          : <MessageSquare size={17} />}
                  </div>
                  <div>
                    <strong>{item.title}</strong>
                    <span>{item.subtitle}</span>
                    <p>{item.text}</p>
                    <time>{formatDate(item.last_message_at)}</time>
                  </div>
                  {Number(item.unread_count || 0) > 1 && <em>{item.unread_count}</em>}
                </button>
              ))}
            </div>
          ) : (
            <div className={styles.panelState}>Нет новых уведомлений.</div>
          )}
        </div>
      )}

      <div className={styles.stack}>
        {toastItems.map(item => (
          <div key={item.toastId} className={styles.card}>
            <button
              type="button"
              className={styles.cardBody}
              onClick={() => openNotification(item)}
            >
              <div className={styles.icon}>
                {item.type === 'document'
                  ? <FileText size={22} />
                  : item.type === 'client_request'
                    ? <Truck size={22} />
                    : <MessageSquare size={22} />}
              </div>
              <div className={styles.content}>
                <strong>{item.title}</strong>
                <span>{item.subtitle}</span>
                <p>{item.text}</p>
              </div>
            </button>
            <button
              type="button"
              className={styles.close}
              onClick={() => dismissToast(item.toastId)}
              aria-label="Скрыть уведомление"
            >
              <X size={16} />
            </button>
          </div>
        ))}
      </div>
    </>
  );

  return (
    <>
      <div className={styles.center}>
      <div className={styles.triggerWrap}>
        <button
          type="button"
          className={`${styles.trigger} ${unreadTotal > 0 ? styles.triggerActive : ''}`}
          onClick={() => setIsPanelOpen(prev => !prev)}
          aria-label="Уведомления тикетов"
        >
          <Bell size={16} />
          {unreadTotal > 0 && <span className={styles.badge}>{badgeLabel}</span>}
        </button>
      </div>
      </div>
      {typeof document !== 'undefined' ? createPortal(overlay, document.body) : overlay}
    </>
  );
};

export default ClientTicketNotifier;
